import React, { useState, useEffect, useRef } from 'react';

/**
 * Composant de sélection de type de camion avec recherche rapide
 * Uniformise l'affichage des types de camions dans toute l'application
 */
const TruckTypeSelect = ({ 
  value, 
  onChange, 
  placeholder = "Rechercher un type de camion...",
  className = "",
  required = false,
  disabled = false,
  includeStandardUnits = false // Pour inclure unité, voyage, etc.
}) => {
  const [truckTypes, setTruckTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Unités standard si besoin
  const standardUnits = includeStandardUnits ? [
    { type: "unité", category: "Unités standard" },
    { type: "voyage", category: "Unités standard" },
    { type: "conteneur", category: "Unités standard" },
    { type: "tonne", category: "Unités standard" },
    { type: "m3", category: "Unités standard" },
    { type: "km", category: "Unités standard" },
    { type: "fourgon", category: "Unités standard" },
    { type: "forfait", category: "Unités standard" }
  ] : [];

  // Charger les types de camions depuis l'API
  useEffect(() => {
    const loadTruckTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/truck-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setTruckTypes([...standardUnits, ...(data.truck_types || [])]);
        }
      } catch (error) {
        console.error('Erreur chargement types camions:', error);
      }
    };

    loadTruckTypes();
  }, []);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les types en fonction de la recherche
  const filteredTypes = truckTypes.filter(truck => 
    truck.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Grouper par catégorie
  const groupedTypes = filteredTypes.reduce((acc, truck) => {
    if (!acc[truck.category]) {
      acc[truck.category] = [];
    }
    acc[truck.category].push(truck);
    return acc;
  }, {});

  // Gérer la sélection
  const handleSelect = (truckType) => {
    onChange(truckType);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Navigation au clavier
  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      e.preventDefault();
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredTypes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredTypes.length) {
          handleSelect(filteredTypes[highlightedIndex].type);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Afficher la valeur sélectionnée
  const displayValue = value || '';

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            )}
          </svg>
        </div>
      </div>

      {/* Dropdown avec résultats */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
          {filteredTypes.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Aucun type trouvé pour "{searchTerm}"
            </div>
          ) : (
            <div>
              {Object.entries(groupedTypes).map(([category, trucks]) => (
                <div key={category}>
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                    {category === "Unités standard" ? "📊 " : "🚛 "}{category}
                  </div>
                  {trucks.map((truck, index) => {
                    const globalIndex = filteredTypes.findIndex(t => t.type === truck.type);
                    return (
                      <button
                        key={truck.type}
                        type="button"
                        onClick={() => handleSelect(truck.type)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${
                          globalIndex === highlightedIndex ? 'bg-blue-100' : ''
                        } ${value === truck.type ? 'bg-emerald-50 font-medium' : ''}`}
                      >
                        {truck.type}
                        {value === truck.type && (
                          <span className="float-right text-emerald-600">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Aide visuelle */}
      {isOpen && filteredTypes.length > 0 && (
        <div className="absolute z-40 w-full mt-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
          💡 Tapez pour filtrer • ↑↓ pour naviguer • Entrée pour sélectionner
        </div>
      )}
    </div>
  );
};

export default TruckTypeSelect;
